import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { request } from "app/network";

interface BodyPartModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    bodyPart?: any; // If provided, it's edit mode
}

const BodyPartModal = ({ open, onClose, onSuccess, bodyPart }: BodyPartModalProps) => {
    const [category, setCategory] = useState("");
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [displayOrder, setDisplayOrder] = useState<number>(0);
    const [isActive, setIsActive] = useState(true);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await request("CommonCode.getByGroup", {
                    pathParams: { groupCode: "INTEREST_AREA_CATEGORY" }
                });
                setCategories(response.data);
            } catch (error) {
                console.error("카테고리 불러오기 실패:", error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        if (bodyPart) {
            setCategory(bodyPart.category || "");
            setName(bodyPart.name || "");
            setCode(bodyPart.code || "");
            setDescription(bodyPart.description || "");
            setDisplayOrder(bodyPart.displayOrder || 0);
            setIsActive(bodyPart.isActive ?? true);
        } else {
            // Create mode: Default to first category if available
            if (categories.length > 0) {
                setCategory(categories[0].code);
            } else {
                setCategory("");
            }
            setName("");
            setCode("");
            setDescription("");
            setDisplayOrder(0);
            setIsActive(true);
        }
    }, [bodyPart, open, categories]);

    const handleSubmit = async () => {
        if (!name || !code) {
            alert("이름과 코드를 입력해주세요.");
            return;
        }

        setLoading(true);
        try {
            if (bodyPart) {
                // Edit mode
                await request("BodyPart.update", {
                    pathParams: { seqId: bodyPart.seqId },
                    paramz: {
                        SeqId: bodyPart.seqId,
                        Category: category,
                        Name: name,
                        Code: code,
                        Description: description,
                        DisplayOrder: displayOrder,
                        IsActive: isActive,
                    }
                });
            } else {
                // Create mode
                await request("BodyPart.create", {
                    paramz: {
                        Category: category,
                        Name: name,
                        Code: code,
                        Description: description,
                        DisplayOrder: displayOrder,
                        IsActive: isActive,
                    }
                });
            }
            onSuccess();
        } catch (error: any) {
            console.error("저장 실패:", error);
            alert(error?.response?.data?.message || "저장에 실패했습니다.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{bodyPart ? "관심부위 수정" : "관심부위 등록"}</DialogTitle>
            <DialogContent>
                <MDBox pt={2} pb={2}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="category-label">카테고리</InputLabel>
                                <Select
                                    labelId="category-label"
                                    value={category}
                                    label="카테고리"
                                    onChange={(e) => setCategory(e.target.value)}
                                    sx={{ height: '44px' }}
                                >
                                    {categories.map((cat) => (
                                        <MenuItem key={cat.code} value={cat.code}>
                                            {cat.name} ({cat.code})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="코드"
                                fullWidth
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="이름"
                                fullWidth
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="설명"
                                fullWidth
                                multiline
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="정렬 순서"
                                fullWidth
                                type="number"
                                value={displayOrder}
                                onChange={(e) => setDisplayOrder(Number(e.target.value))}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                    />
                                }
                                label="활성화"
                            />
                        </Grid>
                    </Grid>
                </MDBox>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} variant="outlined" color="dark">
                    취소
                </MDButton>
                <MDButton
                    onClick={handleSubmit}
                    variant="gradient"
                    color="info"
                    disabled={loading}
                >
                    {loading ? "저장 중..." : "저장"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default BodyPartModal;
